import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

import Types from '../classes/types';
import { renderChildren } from '../utils/reactHelper';
import { lang } from '../services';

import KeyPressInterceptor from './KeyPressInterceptor';
import OutsideClickClose from './OutsideClickClose';
import SvgIcon from './icons/SvgIcon';

const Modal = ({
    showConfirm = (f) => f,
    onClose = (f) => f,
    outsideClickClose = true,
    backdrop = true,
    header = null,
    show = true,
    blockOutsideClickClose,
    outsideConfirm,
    hideClose,
    className,
    children,
}) => {
    const [showingConfirm, setShowingConfirm] = useState(false);
    const [allowClose, setAllowClose] = useState(false);

    useEffect(() => {
        document.body.classList.add('Modal__open');
        setAllowClose(show);

        return () => document.body.classList.remove('Modal__open');
    }, [show])

    const closeConfirm = () => {
        if (!outsideClickClose || showingConfirm || blockOutsideClickClose) {
            return;
        }

        if (!outsideConfirm) {
            onClose();
            return;
        }

        setShowingConfirm(true);

        showConfirm({
            message: lang.t("You have made changes that haven't been saved yet. Are you sure you want to leave this page? Unsaved changes will be lost"),
            okCallback: () => {
                onClose();
            },
            cancelCallback: () => setTimeout(() => setShowingConfirm(false)),
        });
    };

    return (
        createPortal(
            <div
                className={classNames("ModalWrapper", {
                    [className + "Wrapper"]: className,
                    show
                })}
                role="dialog"
            >
                {backdrop && (
                    <div className={classNames('Modal__backdrop', {
                        [className + 'Backdrop']: className,
                        show,
                    })}
                    />
                )}
                <div
                    className={classNames('Modal', className, {
                        show,
                    })}
                    role="dialog"
                    tabIndex="-1"
                >
                    <OutsideClickClose
                        onClose={closeConfirm}
                        enable={allowClose && outsideClickClose}
                    >
                        <KeyPressInterceptor
                            keyCodes={Types.KEY_CODES.ESC}
                            onKeyPressed={closeConfirm}
                        />
                        <div className="Modal__content">
                            {header && (
                                <div className="Modal__header">
                                    {header}
                                </div>
                            )}
                            {!hideClose && (
                                <div
                                    className="Modal__close"
                                    onClick={onClose}
                                >
                                    <SvgIcon icon={SvgIcon.CLOSE} />
                                </div>
                            )}
                            <div className="Modal__body">
                                {renderChildren(children)}
                            </div>
                        </div>
                    </OutsideClickClose>
                </div>
            </div>,
            document.body
        )
    );
}

Modal.propTypes = {
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired,
    header: PropTypes.node,
    outsideClickClose: PropTypes.bool,
    outsideConfirm: PropTypes.bool,
    backdrop: PropTypes.bool,
}

export default Modal;